/**
 * Tour Configuration
 * Central configuration for all tour-related constants, mappings, and settings
 */

import { DriveStep } from 'driver.js';
import { TpiTourKey } from '@typeRoots/common/tourProgress';
import {
  getCompanyLandingTour,
  getCompanyDetailsTour,
  getCompanyAddressTour,
  getVehicleStartTour,
  getVehicleBypassTour,
  getVehicleAddTour,
  getDriverLicenseTour,
  getDriverBypassTour,
  getDriverAddTour,
  getFreightTour,
} from './tourSteps';

/**
 * Master toggle for the tour feature.
 * Set to `false` to disable all tours (auto-start and manual).
 * TourWrapper elements remain in the DOM but tours will not trigger.
 */
export const TOUR_ENABLED = false;

/**
 * Tour Element IDs
 * Single source of truth for all data-tour attribute values
 * Used to ensure consistency between tour steps and component markup
 */
export const TOUR_ELEMENT_IDS = {
  // Company Landing
  COMPANY_BANNER: 'company-banner',
  COMPANY_NAME: 'company-name',
  EDIT_COMPANY_BTN: 'edit-company-btn',
  GET_QUOTE_BTN: 'get-quote-btn',

  // Company Details
  COMPANY_NAME_INPUT: 'company-name-input',
  DBA_INPUT: 'dba-input',
  DOT_NUMBER_INPUT: 'dot-number-input',
  MC_NUMBER_INPUT: 'mc-number-input',
  BUSINESS_START_DATE: 'business-start-date',
  CONTINUE_BTN: 'continue-btn',

  // Company Address
  SAME_ADDRESS_QUESTION: 'same-address-question',
  GARAGING_ADDRESS_INPUT: 'garaging-address-input',
  MAILING_ADDRESS_INPUT: 'mailing-address-input',
  PREVIOUS_AUTHORITY_QUESTION: 'previous-authority-question',
  SAVE_CONTINUE_BTN: 'save-continue-btn',

  // Vehicle Start (VIN Entry)
  VIN_INPUT: 'vin-input',
  UPLOAD_VIN_CARD: 'upload-vin-card',
  VEHICLE_START_NEXT_BTN: 'vehicle-start-next-btn',

  // Vehicle Bypass (Summary)
  VEHICLE_LIST: 'vehicle-list',
  VEHICLE_CARD_ACTIONS: 'vehicle-card-actions',
  ADD_VEHICLE_BTN: 'add-vehicle-btn',

  // Vehicle Add/Edit (Details)
  VEHICLE_TYPE_SWITCH: 'vehicle-type-switch',
  VEHICLE_BASIC_INFO: 'vehicle-basic-info',
  VEHICLE_STATED_VALUE: 'vehicle-stated-value',
  VEHICLE_SAVE_BTN: 'vehicle-save-btn',

  // Driver License Entry
  UPLOAD_DRIVER_LICENSE_CARD: 'upload-driver-license-card',
  DL_NUMBER_INPUT: 'dl-number-input',
  DL_STATE_INPUT: 'dl-state-input',
  DRIVER_LICENSE_NEXT_BTN: 'driver-license-next-btn',

  // Driver Bypass (Summary)
  DRIVER_LIST: 'driver-list',
  DRIVER_CARD_ACTIONS: 'driver-card-actions',
  ADD_DRIVER_BTN: 'add-driver-btn',

  // Driver Add/Edit (Details)
  DRIVER_NAME_FIELDS: 'driver-name-fields',
  DRIVER_EXPERIENCE: 'driver-experience',
  DRIVER_DATE_OF_HIRE: 'driver-date-of-hire',
  DRIVER_ACCIDENTS: 'driver-accidents',
  DRIVER_VIOLATIONS: 'driver-violations',
  DRIVER_SUSPENSIONS: 'driver-suspensions',
  DRIVER_MAJOR_VIOLATIONS: 'driver-major-violations',
  DRIVER_SAVE_BTN: 'driver-save-btn',

  // Freight (Commodities)
  FREIGHT_TITLE: 'freight-title',
  ADD_COMMODITIES_BTN: 'add-commodities-btn',
  COMMODITIES_LIST: 'commodities-list',
  FREIGHT_TOAST: 'freight-toast',
  FREIGHT_NEXT_BTN: 'freight-next-btn',
} as const;

export type TourElementId = (typeof TOUR_ELEMENT_IDS)[keyof typeof TOUR_ELEMENT_IDS];

/**
 * Tour Timing Configuration
 * Centralized timing values for tour behavior
 */
export const TOUR_TIMING = {
  /** Default delay before auto-starting a tour (ms) */
  AUTO_START_DELAY_MS: 2000,

  /** Maximum time to wait for tour elements to be ready (ms) */
  ELEMENT_READY_TIMEOUT_MS: 5000,

  /** Poll interval when checking for element readiness (ms) */
  ELEMENT_CHECK_INTERVAL_MS: 100,

  /** Delay before starting manual tour replay (ms) */
  MANUAL_START_DELAY_MS: 500,

  /** Base delay for retry logic (ms) */
  RETRY_DELAY_BASE_MS: 1000,

  /** Maximum number of retry attempts for backend sync */
  MAX_RETRY_ATTEMPTS: 3,
} as const;

/**
 * Route to Tour Mapping Configuration
 * Maps screen routes to their corresponding tour configurations
 */
export interface TourRouteConfig {
  /** URL pattern to match against window.location.pathname */
  urlPattern: string;

  /** Function that returns tour steps */
  tourSteps: () => DriveStep[];

  /** Human-readable tour title */
  title: string;

  /** Tour key for backend tracking */
  tourKey: TpiTourKey;
}

export const TOUR_ROUTE_MAP: Record<string, TourRouteConfig> = {
  CompanyLandingScreen: {
    urlPattern: 'company-landing',
    tourSteps: getCompanyLandingTour,
    title: 'Company Overview Tour',
    tourKey: 'companyLanding',
  },
  CompanyDetailsScreen: {
    urlPattern: 'company-details',
    tourSteps: getCompanyDetailsTour,
    title: 'Company Details Tour',
    tourKey: 'companyDetails',
  },
  CompanyAddressScreen: {
    urlPattern: 'company-address',
    tourSteps: getCompanyAddressTour,
    title: 'Company Address Tour',
    tourKey: 'companyAddress',
  },
  VehicleStart: {
    urlPattern: '/vehicle',
    tourSteps: getVehicleStartTour,
    title: 'Vehicle VIN Entry Tour',
    tourKey: 'vehicleStart',
  },
  VehicleBypass: {
    urlPattern: 'vehicle-bypass',
    tourSteps: getVehicleBypassTour,
    title: 'Vehicle Summary Tour',
    tourKey: 'vehicleBypass',
  },
  VehiclesEditScreen: {
    urlPattern: 'vehicle-add',
    tourSteps: getVehicleAddTour,
    title: 'Vehicle Details Tour',
    tourKey: 'vehicleAdd',
  },
  DriverStart: {
    urlPattern: 'driver-license',
    tourSteps: getDriverLicenseTour,
    title: 'Driver License Entry Tour',
    tourKey: 'driverStart',
  },
  DriverBypass: {
    urlPattern: 'driver-bypass',
    tourSteps: getDriverBypassTour,
    title: 'Driver Summary Tour',
    tourKey: 'driverBypass',
  },
  EditDriver: {
    urlPattern: 'driver-add',
    tourSteps: getDriverAddTour,
    title: 'Driver Details Tour',
    tourKey: 'driverAdd',
  },
  AddFreightScreen: {
    urlPattern: 'add-freight',
    tourSteps: getFreightTour,
    title: 'Commodities Tour',
    tourKey: 'freightStart',
  },
  // Add more routes as tours are implemented
} as const;

/**
 * Detect route name from URL pathname
 * Fallback when navigation state is unavailable
 */
export function detectRouteFromUrl(pathname: string): string | undefined {
  const entries = Object.entries(TOUR_ROUTE_MAP);

  for (const [routeName, config] of entries) {
    if (pathname.includes(config.urlPattern)) {
      return routeName;
    }
  }

  return undefined;
}

/**
 * Get tour configuration for a given route
 */
export function getTourConfig(routeName: string): TourRouteConfig | undefined {
  return TOUR_ROUTE_MAP[routeName];
}
